import { z } from 'zod';
import { enderecoSchema, soDigitosSchema } from './pessoa.js';
import { ENQUADRAMENTOS, TIPOS_SOCIETARIOS } from './vocabulario.js';
/**
 * Participação de um sócio no capital.
 *
 * `quotas` e `valorNominalCentavos` são a verdade; `valorCentavos` e o
 * percentual são DERIVADOS e nunca armazenados como campo independente —
 * é assim que a soma nunca fecha errada. Nas amostras, o contrato dizia
 * "R$ 50.000,00 dividido em 50.000 quotas" e a tabela imprimia "50,000,00":
 * três números soltos que ninguém conferiu.
 */
/**
 * Papel do sócio na Sociedade em Comandita Simples (art. 1.045 CC):
 * COMANDITADO responde ilimitada e solidariamente e administra; COMANDITÁRIO
 * responde só pelo valor da quota e NÃO administra — se administrar, perde a
 * limitação.
 *
 * Opcional para os demais tipos, onde não faz sentido. Obrigatório na CS —
 * imposto pela regra `cs-sem-comanditado-ou-comanditario`, não pelo schema,
 * porque o schema de participação não enxerga o tipo da sociedade a que
 * pertence.
 */
export const PAPEIS_SOCIO_COMANDITA = ['COMANDITADO', 'COMANDITARIO'];
/**
 * ESPÉCIE da ação, na S.A. e na Comandita por Ações (Lei 6.404/76, art. 15).
 *
 * A diferença é o que cada uma dá: a ordinária dá VOTO; a preferencial dá
 * vantagem patrimonial (dividendo maior, prioridade no reembolso) e em regra
 * não vota. A fruição é rara — nasce da amortização de ações já existentes.
 */
export const ESPECIES_ACAO = ['ORDINARIA', 'PREFERENCIAL', 'FRUICAO'];
export const especieAcaoSchema = z.enum(ESPECIES_ACAO);
export const NOME_ESPECIE_ACAO = {
    ORDINARIA: 'ordinária',
    PREFERENCIAL: 'preferencial',
    FRUICAO: 'de fruição',
};
export const participacaoSchema = z.object({
    pessoaId: z.string().uuid(),
    /**
     * A quantidade de unidades de participação.
     *
     * ⚠️ O NOME diz "quotas" porque foi assim que o sistema nasceu, e nas
     * limitadas é o termo certo. Na S.A. e na Comandita por Ações, o MESMO campo
     * guarda AÇÕES — a estrutura do quadro é idêntica (quem, quanto, desde
     * quando), e o que muda é o vocabulário do documento.
     *
     * Renomear para `unidades` tocaria o schema, o banco, o validador, o gerador
     * e todas as telas para trocar uma palavra que só aparece no código. As
     * cláusulas usam o termo jurídico correto conforme o tipo — ver
     * `termoDaUnidade`.
     */
    quotas: z.number().int().positive(),
    /**
     * Espécie e classe — só nos tipos com AÇÕES (`SA`, `CA`).
     *
     * Uma linha de participação por espécie: o acionista que tem ordinárias e
     * preferenciais aparece duas vezes, porque os direitos de cada bloco são
     * diferentes e o estatuto os descreve separadamente.
     */
    especie: especieAcaoSchema.optional(),
    /** Classe da preferencial (`A`, `B`…). Ordinária de classe única não tem. */
    classe: z.string().max(2).optional(),
    /** Data de ingresso do sócio na sociedade. */
    entrada: z.string().date(),
    /** Preenchida quando o sócio se retira. Sócio com saída não conta no capital. */
    saida: z.string().date().optional(),
    /** Quotas já integralizadas. Igual a `quotas` quando integralizado à vista. */
    quotasIntegralizadas: z.number().int().nonnegative(),
    incomunicavel: z.boolean().default(false),
    impenhoravel: z.boolean().default(false),
    /** Só tem sentido em `CS`. Ver `PapelSocioComandita`. */
    papelSocio: z.enum(PAPEIS_SOCIO_COMANDITA).optional(),
});
/**
 * Poderes do administrador. Lista fechada porque cada item vira uma oração da
 * cláusula de administração — texto livre aqui reintroduz o Word.
 */
/**
 * Quórum de aprovação exigido para designar administrador não sócio
 * (art. 1.061 CC): UNANIMIDADE enquanto o capital não está integralizado,
 * DOIS_TERCOS depois de integralizado.
 */
export const QUORUNS_ADMINISTRADOR_NAO_SOCIO = ['UNANIMIDADE', 'DOIS_TERCOS'];
export const administradorSchema = z.object({
    pessoaId: z.string().uuid(),
    /** Administrador não sócio exige aprovação e registro próprios (art. 1.061 CC). */
    ehSocio: z.boolean(),
    inicio: z.string().date(),
    fim: z.string().date().optional(),
    /** Se pode usar o nome empresarial isoladamente ou só em conjunto. */
    usoIsoladoDoNome: z.boolean().default(true),
    vedadoFiancaAvalEndosso: z.boolean().default(true),
    podeConstituirProcuradores: z.boolean().default(true),
    /**
     * Quórum com que os sócios aprovaram este administrador quando ele não é
     * sócio. Só se aplica a `ehSocio: false`. Ver regra
     * `administrador-nao-socio-sem-quorum`.
     */
    quorumAprovacao: z.enum(QUORUNS_ADMINISTRADOR_NAO_SOCIO).optional(),
    /**
     * Declaração de que o administrador não está impedido de exercer a função
     * (art. 1.011, § 1º, CC). Não dá para o sistema verificar antecedente —
     * só exigir que a declaração exista no cadastro. Ver regra
     * `socio-sem-declaracao-desimpedimento`.
     */
    declaraDesimpedimento: z.boolean().optional(),
});
export const atividadeSchema = z.object({
    /** Código CNAE só com dígitos: `4930202`. A formatação é da camada de redação. */
    cnae: soDigitosSchema(7, 'CNAE'),
    descricao: z.string().min(3),
    principal: z.boolean(),
});
/**
 * FILIAL — estabelecimento secundário da sociedade.
 *
 * ## Por que ela vive DENTRO do estado da sociedade
 * Não é uma entidade autônoma: a filial não tem sócios, não tem contrato
 * próprio e não sobrevive à matriz. Ela é um endereço onde a mesma sociedade
 * opera, e abrir ou fechar uma é ALTERAÇÃO CONTRATUAL — a mesma máquina que
 * já move endereço, capital e quadro societário.
 *
 * Modelá-la como entidade separada criaria um segundo caminho para mudar a
 * sociedade, com uma segunda trilha de atos, e o dia em que os dois
 * divergissem o contrato consolidado não bateria com a realidade registrada.
 *
 * ## O CNPJ compartilha a RAIZ
 * Os oito primeiros dígitos são da matriz; o que muda é a ordem (`0002`,
 * `0003`…). Filial com raiz diferente é outra empresa, e é o erro que o
 * validador denuncia.
 */
export const filialSchema = z.object({
    /**
     * Ausente até a Junta registrar. A filial existe no contrato desde a
     * alteração que a abriu, e ganha CNPJ depois.
     */
    cnpj: z
        .string()
        .regex(/^\d{14}$/, 'CNPJ deve ter 14 dígitos, sem pontuação')
        .optional(),
    nire: z.string().optional(),
    endereco: enderecoSchema,
    /**
     * Objeto próprio, quando a filial não exerce todo o objeto da matriz.
     * Ausente = exerce o mesmo objeto — que é o caso comum.
     */
    objeto: z.string().min(10).optional(),
    /**
     * Capital DESTACADO para a filial, em centavos.
     *
     * Opcional porque a maioria não destaca: o capital continua único da
     * sociedade. Quando há destaque, ele sai no contrato e a Junta o confere
     * contra o capital total.
     */
    capitalDestacadoCentavos: z.number().int().nonnegative().optional(),
    abertura: z.string().date(),
    /** Preenchido quando a filial é encerrada. Ela não some do histórico. */
    encerramento: z.string().date().optional(),
});
/**
 * O ESTADO VIGENTE da sociedade.
 *
 * Nunca é digitado: é o resultado do fold dos atos registrados. Quem quiser
 * mudar alguma coisa aqui registra um ato — não edita este objeto. É essa
 * regra que torna a consolidação automática possível e que impede a
 * divergência entre preâmbulo e texto consolidado vista em
 * `docs/amostras/primeira-alteracao-slu.docx` (registro em 29/01/2024 no
 * preâmbulo, 29/04/2024 na consolidação).
 */
export const sociedadeSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    /**
     * Número sequencial dentro do escritório, começando em 1 — o que a pessoa
     * cita ("a sociedade 42"). Nunca muda, e não se repete no mesmo tenant.
     * Ver `contadorTenant` no schema da API.
     */
    codigo: z.number().int().positive(),
    tipo: z.enum(TIPOS_SOCIETARIOS),
    nomeEmpresarial: z.string().min(3),
    nomeFantasia: z.string().optional(),
    /** Ausente enquanto a constituição não foi registrada na Junta. */
    cnpj: z
        .string()
        .regex(/^\d{14}$/, 'CNPJ deve ter 14 dígitos, sem pontuação')
        .optional(),
    nire: z.string().optional(),
    juntaComercialUf: z.string().length(2).optional(),
    dataRegistro: z.string().date().optional(),
    inicioAtividades: z.string().date(),
    /** `null` = prazo indeterminado, que é a esmagadora maioria. */
    fimPrazoDuracao: z.string().date().nullable().default(null),
    sede: enderecoSchema,
    objetoSocial: z.string().min(10),
    atividades: z.array(atividadeSchema).min(1),
    /** Em CENTAVOS. Dinheiro em float não entra neste repositório. */
    capitalCentavos: z.number().int().positive(),
    /**
     * ⚠️ ZERO significa AÇÃO SEM VALOR NOMINAL — só na S.A. e na Comandita por
     * Ações (Lei 6.404/76, art. 11).
     *
     * Na limitada o valor nominal é obrigatório e positivo; na anônima o
     * estatuto PODE não fixá-lo, e aí o preço de emissão é livre e o capital não
     * se divide por um valor unitário. Era `positive()`, o que tornava a S.A.
     * sem valor nominal irrepresentável.
     *
     * Quem calcula valor de participação tem de conferir isto antes de dividir —
     * ver `valorParticipacaoCentavos`.
     */
    valorNominalQuotaCentavos: z.number().int().nonnegative(),
    enquadramento: z.enum(ENQUADRAMENTOS),
    foroComarca: z.string().min(2),
    foroUf: z.string().length(2),
    /**
     * Prazo (ISO) para integralizar o capital subscrito e ainda não pago.
     * Só faz sentido quando há sócio com `quotasIntegralizadas < quotas`. Sem
     * prazo declarado, a regra `capital-nao-integralizado-sem-prazo` exige.
     */
    prazoIntegralizacao: z.string().date().optional(),
    socios: z.array(participacaoSchema).min(1),
    administradores: z.array(administradorSchema).min(1),
    /**
     * O que só as sociedades por AÇÕES têm. Ausente nos demais tipos.
     *
     * Vive num objeto próprio, e não solto no schema: são sete campos que não
     * fazem sentido nenhum numa limitada, e espalhá-los faria toda sociedade
     * carregar sete nulos para expressar "não sou uma S.A.".
     */
    acoes: z
        .object({
        /**
         * ABERTA negocia em bolsa ou balcão; FECHADA não.
         *
         * Muda o que o estatuto precisa ter: a aberta exige Conselho de
         * Administração (art. 138, §2º) e registro na CVM. Este produto monta o
         * estatuto das duas, mas a aberta tem obrigações fora daqui.
         */
        aberta: z.boolean().default(false),
        /**
         * Capital AUTORIZADO, em centavos — o teto até onde a administração pode
         * emitir sem alterar o estatuto (art. 168).
         *
         * Ausente = não há autorização; toda emissão passa por assembleia.
         */
        capitalAutorizadoCentavos: z.number().int().positive().optional(),
        /** Há Conselho de Administração? Obrigatório na aberta. */
        conselhoAdministracao: z.boolean().default(false),
        /** Nº de conselheiros, quando há conselho. Mínimo 3 (art. 140). */
        conselheiros: z.number().int().min(3).optional(),
        /**
         * Conselho Fiscal PERMANENTE, ou só instalado a pedido de acionistas
         * (art. 161). O estatuto tem de dizer qual dos dois.
         */
        conselhoFiscalPermanente: z.boolean().default(false),
        /**
         * Dividendo mínimo obrigatório, em centésimos de ponto percentual.
         * `2500` = 25%, que é o default legal quando o estatuto silencia
         * (art. 202, §2º).
         */
        dividendoMinimoBps: z.number().int().min(0).max(10_000).default(2_500),
        /** Vantagem das preferenciais, como sai no estatuto (art. 17). */
        vantagemPreferenciais: z.string().min(10).optional(),
    })
        .optional(),
    /**
     * As filiais, abertas e encerradas. Ausente é o caso comum.
     *
     * `optional()` e não `default([])`: com `default`, o Zod torna o campo
     * OBRIGATÓRIO no tipo de saída, e toda sociedade construída no código
     * passaria a exigir um `filiais: []` explícito — ruído em cada teste, cada
     * seed e cada fixture, para expressar o normal.
     *
     * Quem lê usa `?? []`. Sociedade sem filial é a esmagadora maioria.
     */
    filiais: z.array(filialSchema).optional(),
});
/** Sócios ainda na sociedade numa data. Sócio retirado não compõe o capital. */
export function sociosVigentes(s, em) {
    const data = em ?? '9999-12-31';
    return s.socios.filter((p) => p.entrada <= data && (!p.saida || p.saida > data));
}
/** Total de quotas subscritas pelos sócios vigentes. */
export function quotasSubscritas(s, em) {
    return sociosVigentes(s, em).reduce((total, p) => total + p.quotas, 0);
}
/**
 * Percentual de um sócio, sempre DERIVADO das quotas.
 * Retorna 0 quando não há quotas — divisão por zero é problema de outra regra.
 */
export function percentualDe(s, pessoaId, em) {
    const total = quotasSubscritas(s, em);
    if (total === 0)
        return 0;
    const dele = sociosVigentes(s, em)
        .filter((p) => p.pessoaId === pessoaId)
        .reduce((soma, p) => soma + p.quotas, 0);
    return (dele / total) * 100;
}
/**
 * Valor em centavos da participação, derivado de quotas × valor nominal.
 *
 * Devolve `null` quando NÃO HÁ valor nominal — a S.A. sem valor nominal
 * (art. 11 da Lei 6.404/76). Devolver zero ali seria pior que devolver nada:
 * a tabela de capital imprimiria "R$ 0,00" ao lado de cada acionista, como se
 * a participação não valesse nada, quando na verdade ela não se calcula assim.
 */
export function valorParticipacaoCentavos(s, p) {
    if (s.valorNominalQuotaCentavos === 0)
        return null;
    return p.quotas * s.valorNominalQuotaCentavos;
}
/**
 * O termo jurídico da unidade de participação, conforme o tipo.
 *
 * Quota na limitada, AÇÃO na anônima e na comandita por ações. O campo do
 * schema se chama `quotas` nos dois casos (ver o comentário lá); é esta função
 * que faz o DOCUMENTO usar a palavra certa — e escrever "quotas" num estatuto
 * de S.A. é o tipo de erro que a Junta devolve sem ler o resto.
 */
export function termoDaUnidade(tipo) {
    return tipo === 'SA'
        ? { singular: 'ação', plural: 'ações' }
        : { singular: 'quota', plural: 'quotas' };
}
/** Os tipos cujo capital se divide em AÇÕES, e cujo documento é ESTATUTO. */
export function ehPorAcoes(tipo) {
    /*
      Só `SA`. A Comandita por Ações também emite ações e também se rege por
      estatuto, mas não está em `TIPOS_SOCIETARIOS` — e um ramo aqui para um tipo
      que o sistema não oferece seria código que ninguém executa e que o próximo
      leitor tomaria por suporte existente. Quando a CA entrar, entra aqui.
    */
    return tipo === 'SA';
}
/**
 * Entrada de criação da sociedade, com sócios, administradores e CNAEs juntos.
 *
 * Tudo num corpo só, e não em quatro chamadas: uma sociedade sem sócio ou sem
 * administrador é estado inválido, e deixar o cliente construir esse estado em
 * etapas cria uma janela em que o banco guarda algo que o domínio proíbe.
 *
 * Sem `id` nem `tenantId` — os dois nascem no servidor.
 */
/**
 * O `codigo` fica de fora: quem cria não escolhe o número — o servidor o
 * reserva no contador do tenant. Aceitá-lo do cliente deixaria dois cadastros
 * pedirem o mesmo.
 */
export const criarSociedadeSchema = sociedadeSchema.omit({
    id: true,
    tenantId: true,
    codigo: true,
});
/**
 * A ALTERAÇÃO contratual: o estado novo por inteiro, mais o que só quem faz a
 * alteração sabe.
 *
 * O corpo é o estado que a sociedade passa a ter — não uma lista de operações
 * ("mude o endereço", "admita o sócio"). O motivo é o mesmo do `PATCH` de
 * pessoa: a tela já tem o estado inteiro na mão, e um formato de operações
 * exigiria que ela derivasse quais foram — que é justamente o trabalho que
 * `compararSociedades` faz melhor, no servidor, com o estado gravado à frente.
 *
 * `data` é a data do ATO, não a de hoje: a alteração costuma ser assinada numa
 * data e lançada no sistema noutra, e é a data do instrumento que a Junta lê.
 *
 * `cessoes` existe porque cessão de quotas não se infere de comparação de
 * estado — ver `compararSociedades`. Sem esta declaração, uma cessão sairia no
 * documento como simples mudança de participação, sem a anuência dos demais
 * sócios que a cessão exige.
 */
export const cessaoQuotasSchema = z.object({
    cedenteId: z.string().uuid(),
    cessionarioId: z.string().uuid(),
    quotas: z.number().int().positive(),
    /** Preço da cessão em centavos. `null` quando é doação ou permuta. */
    valorCentavos: z.number().int().nonnegative().nullable().default(null),
});
export const alterarSociedadeSchema = criarSociedadeSchema.extend({
    data: z.string().date(),
    cessoes: z.array(cessaoQuotasSchema).default([]),
});
//# sourceMappingURL=sociedade.js.map